import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "363",
  "slug": "what-are-antipodes",
  "title": "What Are Antipodes? The Point on the Exact Opposite Side of Earth",
  "excerpt": "If you dug straight down through the centre of the Earth, where would you come out? Almost certainly the ocean. Here's what antipodes are and why 'digging to China' is mostly a myth.",
  "date": "2026-06-07",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxlYXJ0aCUyMGdsb2JlJTIwc3BhY2V8ZW58MXwwfHx8MTc4MDg0NTQyMnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Your antipode is the point on Earth's surface directly opposite you, reached by drilling straight through the centre of the planet. Because Earth is mostly ocean, the vast majority of land has its antipode in the sea.",
  "content": [
    {
      "type": "paragraph",
      "text": "Stand anywhere on Earth, point straight down through your feet, and imagine a line passing through the centre of the planet and out the other side. The spot where it emerges is your antipode, the single point on the globe that is as far from you as it is possible to be. The word comes from Greek and literally means opposite-footed, the idea being that someone standing there would have the soles of their feet pointing back toward yours."
    },
    {
      "type": "heading",
      "text": "How to Find Your Antipode"
    },
    {
      "type": "paragraph",
      "text": "The maths is simple. To find the antipode of any location, flip the latitude from north to south (or south to north) and shift the longitude by 180 degrees. A place at 40 degrees north and 74 degrees west, roughly New York, has an antipode at 40 degrees south and 106 degrees east, a patch of the Indian Ocean southwest of Australia. Because the calculation is so clean, antipode maps are easy to generate, and they reveal something surprising about how the planet is arranged."
    },
    {
      "type": "heading",
      "text": "Why Most Antipodes Are Underwater"
    },
    {
      "type": "paragraph",
      "text": "Oceans cover about 71 percent of Earth's surface, and land is distributed very unevenly, with most of it sitting in the Northern Hemisphere. The consequence is that if you pick a random point on land, its antipode is overwhelmingly likely to be ocean. In fact, only a small fraction of land has another piece of land on the exact opposite side. So for most people reading this, the honest answer to what is directly beneath me on the far side is: deep seawater."
    },
    {
      "type": "heading",
      "text": "The Land-to-Land Exceptions"
    },
    {
      "type": "paragraph",
      "text": "There are some neat exceptions. A large chunk of South America is antipodal to East and Southeast Asia; parts of Argentina and Chile sit opposite China and the surrounding region. Spain and Portugal are roughly antipodal to New Zealand, which is why New Zealanders sometimes joke about Spain being directly below them. Greenland is opposite a slice of Antarctica. These land-to-land pairs are the exception rather than the rule, which is exactly what makes them fun to track down."
    },
    {
      "type": "heading",
      "text": "The Longest Journey on Earth"
    },
    {
      "type": "paragraph",
      "text": "Your antipode is also, by definition, the farthest you could ever travel across the surface of the planet without leaving it. Reaching it means covering roughly 20,000 kilometres, half the circumference of the Earth, no matter which direction you set off in. Very few commercial flights come close to connecting true antipodes, partly because so few pairs of large cities sit opposite one another, and partly because such a route would push against the practical limit of how far an aircraft can fly without refuelling. The handful of record-breaking ultra-long-haul flights that exist today still fall well short of a genuine antipodal hop."
    },
    {
      "type": "heading",
      "text": "The Truth About 'Digging to China'"
    },
    {
      "type": "paragraph",
      "text": "Generations of children in the United States have been told that if they dug a hole straight down, they would end up in China. Geographically, that is almost entirely wrong. The antipode of the continental United States lands in the Indian Ocean, well away from any country. If you actually wanted to dig from China and surface on land, you would have a better shot starting in Argentina or Chile. Digging to the Indian Ocean just never caught on as a phrase."
    },
    {
      "type": "heading",
      "text": "Antipodes and the Edge of the Map"
    },
    {
      "type": "paragraph",
      "text": "Antipodes also help explain a feature that confuses a lot of people: the International Date Line. It runs roughly along the 180-degree meridian, the longitude exactly opposite the Prime Meridian in Greenwich. Cross it heading west and you jump forward a day; cross it heading east and you repeat one. The line zigzags to avoid splitting countries, but its backbone is that 180-degree antipodal line on the far side of the world from where global timekeeping begins."
    },
    {
      "type": "heading",
      "text": "A Geographer's Party Trick"
    },
    {
      "type": "paragraph",
      "text": "Once you internalise the rough antipode pairings, you have a quiet superpower for thinking about the globe. You start to grasp just how much of the planet is water, how lopsided the land really is, and how far apart opposite places truly are. It is the kind of spatial intuition that separates people who can picture the whole Earth in their head from people who only know it as a flat rectangle."
    }
  ]
};
